import React, { useState, useEffect, useMemo } from "react";
import Header from "../components/layout/Header";
import BottomNav from "../components/layout/BottomNav";
import {
  PlanSessionModal,
  EditSessionModal,
  StartSessionModal,
} from "../components/sessions";
import {
  HiPlus,
  HiCalendar,
  HiClock,
  HiPencil,
  HiTrash,
  HiPlay,
  HiCog6Tooth,
  HiFunnel,
  HiXMark,
} from "react-icons/hi2";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { sessionService } from "../services/sessionService";
import { activityService } from "../services/activityService";
import { GlobalActivityService } from "../services/globalActivityService";
import { Session, SessionTemplate, ActivityMeasurement } from "../types/post";
import { ActivityType } from "../types/activity";
import { asDisplayNameOrNull } from "../utils/displayName";

const Sessions = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const [isPlanSessionModalOpen, setIsPlanSessionModalOpen] = useState(false);
  const [isEditSessionModalOpen, setIsEditSessionModalOpen] = useState(false);
  const [isStartSessionModalOpen, setIsStartSessionModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<
    (Session & { id: string }) | null
  >(null);
  const [selectedTemplate, setSelectedTemplate] = useState<
    (SessionTemplate & { id: string }) | null
  >(null);
  const [sessionToDelete, setSessionToDelete] = useState<
    (Session & { id: string }) | null
  >(null);
  const [sessions, setSessions] = useState<(Session & { id: string })[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, ActivityType[]>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(
    null,
  );

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sessionTypeFilter, setSessionTypeFilter] = useState<string>("all");

  // Fetch sessions, activity types, and session Sessions
  const fetchData = async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      setError(null);

      const [sessionsData, activityTypesData] = await Promise.all([
        sessionService.getAllSessions(currentUser.uid),
        activityService.fetchActivityTypes(currentUser.uid),
      ]);

      setSessions(sessionsData);
      setActivityTypes(activityTypesData);

      // Load all global categories and their activities so name matching
      // works even if the current user hasn't added those packs.
      const categories = await GlobalActivityService.fetchCategories();
      const categoryIds = categories
        .map((category) => category.id)
        .filter((id): id is string => !!id);

      if (categoryIds.length > 0) {
        const allGlobalActivities =
          await GlobalActivityService.fetchActivityTypesForCategories(
            categoryIds,
          );
        const grouped = allGlobalActivities.reduce<
          Record<string, ActivityType[]>
        >((acc, type) => {
          const categoryId = type.categoryId;
          if (!categoryId) return acc;
          if (!acc[categoryId]) acc[categoryId] = [];
          acc[categoryId].push({
            id: type.id,
            name: type.name,
            description: type.description,
            variables: type.variables,
            image: type.image,
            isFromPack: true,
            categoryId: type.categoryId,
          });
          return acc;
        }, {});
        setGlobalActivityTypes(grouped);
      } else {
        setGlobalActivityTypes({});
      }
    } catch (error) {
      console.error("Error fetching sessions data:", error);
      setError("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  const handlePlanSessionClick = () => {
    setIsPlanSessionModalOpen(true);
    setSelectedTemplate(null); // Start from scratch by default
  };

  const handleSessionCreated = (sessionIds: string[] = []) => {
    if (sessionIds.length > 0) {
      console.log("Sessions created with IDs:", sessionIds);
    }
    // Refresh the sessions list
    fetchData();
    // Clear selected template
    setSelectedTemplate(null);
  };

  const handleSessionUpdated = (sessionId: string) => {
    console.log("Session updated with ID:", sessionId);
    // Refresh the sessions list
    fetchData();
    setSelectedSession(null);
  };

  const handleEditSession = (session: Session & { id: string }) => {
    setSelectedSession(session);
    setIsEditSessionModalOpen(true);
  };

  const handleDeleteSession = async (session: Session & { id: string }) => {
    if (!currentUser?.uid) return;

    try {
      setDeletingSessionId(session.id);
      await sessionService.deleteSession(currentUser.uid, session.id);
      console.log("Session deleted successfully");
      // Refresh the sessions list
      fetchData();
      setSessionToDelete(null);
    } catch (error) {
      console.error("Error deleting session:", error);
      setError("Failed to delete session");
    } finally {
      setDeletingSessionId(null);
    }
  };

  const handleDeleteClick = (session: Session & { id: string }) => {
    setSessionToDelete(session);
  };

  const handleCancelDelete = () => {
    setSessionToDelete(null);
  };

  const handleStartSession = (session: Session & { id: string }) => {
    navigate(`/session/${session.id}`);
  };

  const handleStartSessionClick = () => {
    setIsStartSessionModalOpen(true);
  };

  const handleStartSessionCreated = (sessionId: string) => {
    setIsStartSessionModalOpen(false);
    // Navigation is handled by StartSessionModal
  };

  const getRawActivityId = (activityTypeId: string): string => {
    if (activityTypeId.startsWith("global_")) {
      const parts = activityTypeId.split("_");
      if (parts.length >= 3) {
        return parts.slice(2).join("_");
      }
    }
    return activityTypeId;
  };

  const getCategoryIdFromActivityTypeId = (
    activityTypeId: string,
  ): string | undefined => {
    if (!activityTypeId.startsWith("global_")) return undefined;
    const parts = activityTypeId.split("_");
    if (parts.length >= 3) return parts[1];
    return undefined;
  };

  const getActivityTypeName = (measurement: ActivityMeasurement) => {
    const rawActivityId = getRawActivityId(measurement.ActivityTypeID);
    const activityType = activityTypes.find(
      (type) => type.id === rawActivityId,
    );
    if (activityType) return activityType.name;

    const categoryId =
      measurement.globalCategoryID ||
      getCategoryIdFromActivityTypeId(measurement.ActivityTypeID);
    if (categoryId) {
      const categoryTypes = globalActivityTypes[categoryId] || [];
      const globalMatch = categoryTypes.find(
        (type) => type.id === rawActivityId,
      );
      if (globalMatch) return globalMatch.name;
    }

    for (const categoryTypes of Object.values(globalActivityTypes)) {
      const globalMatch = categoryTypes.find(
        (type) => type.id === rawActivityId,
      );
      if (globalMatch) return globalMatch.name;
    }

    return "Unknown Activity";
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "planned":
        return "bg-blue-500/15 text-blue-700 dark:text-blue-400";
      case "executing":
        return "bg-orange-500/15 text-orange-700 dark:text-orange-400";
      case "complete":
        return "bg-green-500/15 text-green-700 dark:text-green-400";
      default:
        return "bg-base-200 text-base-content";
    }
  };

  const getStatusBorderAndBg = (status: string) => {
    switch (status) {
      case "planned":
        return "border-l-4 border-blue-500 bg-blue-500/10";
      case "executing":
        return "border-l-4 border-orange-500 bg-orange-500/10";
      case "complete":
        return "border-l-4 border-green-500 bg-green-500/10";
      default:
        return "border-l-4 border-base-content/60 bg-base-200/30";
    }
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayString = () => {
    return new Date().toISOString().split("T")[0];
  };

  const resolveSessionTypeName = (sessionTypeID?: string): string | null => {
    if (!sessionTypeID || typeof sessionTypeID !== "string") return null;
    const trimmed = sessionTypeID.trim();
    if (!trimmed) return null;

    // Admin session types are often stored as composite IDs: `${sportId}:${sessionTypeName}`
    if (trimmed.includes(":")) {
      return trimmed.split(":")[1] || null;
    }

    // Fallback: if it's already a readable string, use it
    return trimmed;
  };

  const getFirstActivityName = (session: Session): string | null => {
    const first = session.ActivityMeasurementList?.[0];
    if (!first) return null;
    const name = getActivityTypeName(first);
    return name && name !== "Unknown Activity" ? name : null;
  };

  const getDisplayTitle = (session: Session): string => {
    return (
      asDisplayNameOrNull(session.name) ||
      resolveSessionTypeName(session.sessionTypeID) ||
      getFirstActivityName(session) ||
      `Session - ${formatDate(session.plannedDate)}`
    );
  };

  // Helper function to compare dates
  const isDateBeforeToday = (dateString: string) => {
    const today = getTodayString();
    return dateString < today;
  };

  // Build unique session type options for the filter
  const sessionTypeOptions = useMemo(() => {
    const typeMap = new Map<string, string>();
    for (const session of sessions) {
      if (session.sessionTypeID) {
        const name = resolveSessionTypeName(session.sessionTypeID);
        if (name) typeMap.set(session.sessionTypeID, name);
      }
    }
    return Array.from(typeMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [sessions, userData]);

  const hasActiveFilters =
    statusFilter !== "all" || sessionTypeFilter !== "all";

  const resetFilters = () => {
    setStatusFilter("all");
    setSessionTypeFilter("all");
  };

  // Filter and sort sessions
  const activeSessions = sessions
    .filter((session) => {
      // Base visibility: executing or future planned
      if (session.status === "executing") {
        // ok
      } else if (session.status === "planned") {
        if (isDateBeforeToday(session.plannedDate)) return false;
      } else if (session.status === "complete") {
        // Only show completed if explicitly filtered
        if (statusFilter !== "complete") return false;
      } else {
        return false;
      }

      // Status filter
      if (statusFilter !== "all" && session.status !== statusFilter) {
        return false;
      }

      // Session type filter
      if (sessionTypeFilter !== "all") {
        if (session.sessionTypeID !== sessionTypeFilter) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const today = getTodayString();

      // First, prioritize today's sessions
      const aIsToday = a.plannedDate === today;
      const bIsToday = b.plannedDate === today;

      if (aIsToday && !bIsToday) return -1;
      if (!aIsToday && bIsToday) return 1;

      // Then sort by status priority: executing > planned
      const statusPriority: Record<string, number> = {
        executing: 2,
        planned: 1,
      };
      const statusDiff =
        (statusPriority[b.status] || 0) - (statusPriority[a.status] || 0);
      if (statusDiff !== 0) return statusDiff;

      // Finally, sort by date (newest first)
      return b.plannedDate.localeCompare(a.plannedDate);
    });

  // Split sessions into today and future
  const today = getTodayString();
  const todaySessions = activeSessions.filter((s) => s.plannedDate === today);
  const futureSessions = activeSessions.filter((s) => s.plannedDate !== today);

  return (
    <div className="min-h-screen bg-base-200">
      <Header />
      <div className="pt-16 pb-24">
        <div className="max-w-4xl mx-auto px-8 sm:px-6 lg:px-8">
          <div className="py-8">
            {/* Header with Session Buttons */}
            <div className="mb-6">
              <div className="mb-4">
                <div className="flex items-center">
                  <h1 className="text-3xl sm:text-3xl font-bold text-base-content font-heading">
                    Sessions & Activities
                  </h1>
                  <div className="text-xs bg-lime font-grotesk text-black font-bold px-2 py-1 rounded-full ml-2">
                    beta
                  </div>
                </div>
                <p className="text-sm sm:text-base text-base-content/70 mt-1 font-grotesk">
                  Plan Training Sessions and track of progress without
                  spreadsheets.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <button
                  onClick={handleStartSessionClick}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
                >
                  <HiPlay className="text-base" />
                  Start Session
                </button>
                <button
                  onClick={handlePlanSessionClick}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-base-content/80 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
                >
                  <HiPlus className="text-base" />
                  Schedule Session
                </button>
                <button
                  onClick={() => navigate("/sessions")}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-base-content/80 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors font-grotesk"
                >
                  {/* <HiCog6Tooth className="text-base" /> */}
                  <span className="hidden sm:inline">Session Library</span>
                  <span className="sm:hidden">Saved Sessions & Exercises</span>
                </button>
              </div>
            </div>

            {/* Filter Controls */}
            <div className="mb-4 flex items-center gap-2">
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors font-grotesk ${
                  showFilters || hasActiveFilters
                    ? "bg-base-content text-base-100 border-base-content"
                    : "bg-base-100 text-base-content/70 border-base-300 hover:bg-base-200"
                }`}
              >
                <HiFunnel className="w-4 h-4" />
                Filter
                {hasActiveFilters && (
                  <span className="w-2 h-2 rounded-full bg-base-100" />
                )}
              </button>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-base-300 bg-base-100 text-base-content/70 hover:bg-base-200 transition-colors font-grotesk"
                >
                  <HiXMark className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>

            {showFilters && (
              <div className="mb-6 p-4 bg-base-100 rounded-lg border border-base-300">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-base-content/60 font-grotesk mb-1">
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-base-300 bg-base-200 text-base-content font-grotesk focus:outline-none focus:ring-1 focus:ring-base-content/30"
                    >
                      <option value="all">All Statuses</option>
                      <option value="planned">Planned</option>
                      <option value="executing">Executing</option>
                      <option value="complete">Complete</option>
                    </select>
                  </div>
                  {sessionTypeOptions.length > 0 && (
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-base-content/60 font-grotesk mb-1">
                        Session Type
                      </label>
                      <select
                        value={sessionTypeFilter}
                        onChange={(e) => setSessionTypeFilter(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-base-300 bg-base-200 text-base-content font-grotesk focus:outline-none focus:ring-1 focus:ring-base-content/30"
                      >
                        <option value="all">All Types</option>
                        {sessionTypeOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sessions Content */}
            {loading ? (
              <div className="bg-base-100 rounded-lg shadow p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-base-300 rounded w-1/3"></div>
                  <div className="space-y-3">
                    <div className="h-20 bg-base-300 rounded"></div>
                    <div className="h-20 bg-base-300 rounded"></div>
                    <div className="h-20 bg-base-300 rounded"></div>
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="bg-base-100 rounded-lg shadow p-6">
                <div className="bg-red-500/10 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600">{error}</p>
                  <button
                    onClick={fetchData}
                    className="mt-2 text-sm text-red-700 hover:text-red-800 underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            ) : activeSessions.length === 0 ? (
              <div className="text-center p-8">
                <HiCalendar className="mx-auto text-base-content/50 text-3xl sm:text-4xl mb-4" />
                <h3 className="sm:text-lg text-base-content mb-2 font-heading">
                  No Scheduled Sessions
                </h3>
                <p className="text-sm sm:text-base text-base-content/70 mb-4 font-grotesk">
                  Start a session now to begin tracking your workout, or plan
                  one for later.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Today's Sessions Section */}
                {todaySessions.length > 0 && (
                  <div>
                    <div className="mb-4 px-3 sm:px-4 py-2 bg-blue-500/10 border-l-4 border-blue-500 rounded-lg">
                      <h2 className="text-base sm:text-lg font-semibold text-base-content font-heading">
                        Today
                      </h2>
                      <p className="text-xs sm:text-sm text-base-content/70 font-grotesk">
                        {todaySessions.length}{" "}
                        {todaySessions.length === 1 ? "session" : "sessions"}
                      </p>
                    </div>
                    <div className="space-y-4">
                      {todaySessions.map((session) => (
                        <div
                          key={session.id}
                          className={`bg-base-100 rounded-lg shadow p-4 sm:p-6 ${getStatusBorderAndBg(
                            session.status,
                          )}`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                                <h3 className="text-base sm:text-lg font-semibold text-base-content font-heading truncate">
                                  {getDisplayTitle(session)}
                                </h3>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                                    session.status,
                                  )} flex-shrink-0 font-grotesk`}
                                >
                                  {session.status.charAt(0).toUpperCase() +
                                    session.status.slice(1)}
                                </span>
                              </div>

                              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-base-content/70 mb-3">
                                <div className="flex items-center gap-1">
                                  <HiCalendar className="text-xs flex-shrink-0" />
                                  <span className="font-grotesk">
                                    {formatDate(session.plannedDate)}
                                  </span>
                                </div>
                                {session.startTime && (
                                  <div className="flex items-center gap-1">
                                    <HiClock className="text-xs flex-shrink-0" />
                                    <span className="font-grotesk">
                                      Started:{" "}
                                      {new Date(
                                        session.startTime,
                                      ).toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </div>
                                )}
                                {session.endTime && (
                                  <div className="flex items-center gap-1">
                                    <HiClock className="text-xs flex-shrink-0" />
                                    <span className="font-grotesk">
                                      Completed:{" "}
                                      {new Date(
                                        session.endTime,
                                      ).toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2 sm:ml-4 flex-shrink-0">
                              {session.status === "planned" ? (
                                <button
                                  onClick={() => handleStartSession(session)}
                                  disabled={deletingSessionId === session.id}
                                  className="flex items-center gap-1.5 sm:gap-2 px-3 py-2 text-xs sm:text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk whitespace-nowrap"
                                  title="Start session"
                                >
                                  <HiPlay className="text-sm" />
                                  Start
                                </button>
                              ) : session.status === "executing" ? (
                                <button
                                  onClick={() => handleStartSession(session)}
                                  disabled={deletingSessionId === session.id}
                                  className="flex items-center gap-1.5 sm:gap-2 px-3 py-2 text-xs sm:text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk whitespace-nowrap"
                                  title="View session"
                                >
                                  <HiPlay className="text-sm" />
                                  View
                                </button>
                              ) : null}

                              <button
                                onClick={() => handleEditSession(session)}
                                disabled={deletingSessionId === session.id}
                                className="p-2 text-base-content/70 hover:text-blue-600 hover:bg-blue-500/10 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                                title="Edit session"
                              >
                                <HiPencil className="text-base" />
                              </button>

                              <button
                                onClick={() => handleDeleteClick(session)}
                                disabled={deletingSessionId === session.id}
                                className="p-2 text-base-content/70 hover:text-red-600 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                                title="Delete session"
                              >
                                {deletingSessionId === session.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                ) : (
                                  <HiTrash className="text-base" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Activities */}
                          <div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                              <h4 className="text-sm font-medium text-base-content font-heading">
                                Activities (
                                {session.ActivityMeasurementList?.length || 0})
                              </h4>
                              {session.ActivityMeasurementList &&
                                session.ActivityMeasurementList.length > 0 &&
                                (() => {
                                  const loggedCount =
                                    session.ActivityMeasurementList.filter(
                                      (m) => !!m.timeLogged,
                                    ).length;
                                  const totalCount =
                                    session.ActivityMeasurementList.length;
                                  const progressPercentage =
                                    (loggedCount / totalCount) * 100;
                                  return (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-base-content/70 font-grotesk whitespace-nowrap">
                                        {loggedCount}/{totalCount} logged
                                      </span>
                                      <div className="w-20 sm:w-24 h-2 bg-base-300 rounded-full overflow-hidden flex-shrink-0">
                                        <div
                                          className="h-full bg-green-500 transition-all duration-300"
                                          style={{
                                            width: `${progressPercentage}%`,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })()}
                            </div>
                            <div className="space-y-2">
                              {session.ActivityMeasurementList?.map(
                                (measurement, index) => {
                                  const isLogged = !!measurement.timeLogged;
                                  return (
                                    <div
                                      key={index}
                                      className={`flex items-center justify-between p-2.5 sm:p-3 rounded-lg border ${
                                        isLogged
                                          ? "bg-green-500/10 border-green-200"
                                          : "bg-base-200 border-base-300"
                                      }`}
                                    >
                                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                        <div
                                          className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            isLogged
                                              ? "bg-green-500 text-white"
                                              : "bg-base-300"
                                          }`}
                                        >
                                          {isLogged && (
                                            <svg
                                              className="w-2.5 h-2.5 sm:w-3 sm:h-3"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          )}
                                        </div>
                                        <span
                                          className={`text-sm sm:text-base font-medium truncate ${
                                            isLogged
                                              ? "text-green-800 dark:text-green-300"
                                              : "text-base-content"
                                          } font-grotesk`}
                                        >
                                          {getActivityTypeName(measurement)}
                                        </span>
                                      </div>

                                      {isLogged ? (
                                        <span className="text-xs font-medium text-green-700 flex-shrink-0 ml-2 font-grotesk">
                                          Logged
                                        </span>
                                      ) : (
                                        <span className="text-xs text-base-content/60 flex-shrink-0 ml-2 font-grotesk">
                                          Planned
                                        </span>
                                      )}
                                    </div>
                                  );
                                },
                              ) || (
                                <p className="text-sm text-base-content/60 font-grotesk">
                                  No activities
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Sessions Section */}
                {futureSessions.length > 0 && (
                  <div>
                    <div className="mb-4 px-3 sm:px-4 py-2 bg-base-200 border-l-4 border-base-content/50 rounded-lg">
                      <h2 className="text-base sm:text-lg font-semibold text-base-content font-heading">
                        Upcoming
                      </h2>
                      <p className="text-xs sm:text-sm text-base-content/70 font-grotesk">
                        {futureSessions.length}{" "}
                        {futureSessions.length === 1 ? "session" : "sessions"}
                      </p>
                    </div>
                    <div className="space-y-4">
                      {futureSessions.map((session) => (
                        <div
                          key={session.id}
                          className={`bg-base-100 rounded-lg shadow p-4 sm:p-6 ${getStatusBorderAndBg(
                            session.status,
                          )}`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                                <h3 className="text-base sm:text-lg font-semibold text-base-content font-heading truncate">
                                  {getDisplayTitle(session)}
                                </h3>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                                    session.status,
                                  )} flex-shrink-0 font-grotesk`}
                                >
                                  {session.status.charAt(0).toUpperCase() +
                                    session.status.slice(1)}
                                </span>
                              </div>

                              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-base-content/70 mb-3">
                                <div className="flex items-center gap-1">
                                  <HiCalendar className="text-xs flex-shrink-0" />
                                  <span className="font-grotesk">
                                    {formatDate(session.plannedDate)}
                                  </span>
                                </div>
                                {session.startTime && (
                                  <div className="flex items-center gap-1">
                                    <HiClock className="text-xs flex-shrink-0" />
                                    <span className="font-grotesk">
                                      Started:{" "}
                                      {new Date(
                                        session.startTime,
                                      ).toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </div>
                                )}
                                {session.endTime && (
                                  <div className="flex items-center gap-1">
                                    <HiClock className="text-xs flex-shrink-0" />
                                    <span className="font-grotesk">
                                      Completed:{" "}
                                      {new Date(
                                        session.endTime,
                                      ).toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2 sm:ml-4 flex-shrink-0">
                              {session.status === "planned" ? (
                                <button
                                  onClick={() => handleStartSession(session)}
                                  disabled={deletingSessionId === session.id}
                                  className="flex items-center gap-1.5 sm:gap-2 px-3 py-2 text-xs sm:text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk whitespace-nowrap"
                                  title="Start session"
                                >
                                  <HiPlay className="text-sm" />
                                  Start
                                </button>
                              ) : session.status === "executing" ? (
                                <button
                                  onClick={() => handleStartSession(session)}
                                  disabled={deletingSessionId === session.id}
                                  className="flex items-center gap-1.5 sm:gap-2 px-3 py-2 text-xs sm:text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk whitespace-nowrap"
                                  title="View session"
                                >
                                  <HiPlay className="text-sm" />
                                  View
                                </button>
                              ) : null}

                              <button
                                onClick={() => handleEditSession(session)}
                                disabled={deletingSessionId === session.id}
                                className="p-2 text-base-content/70 hover:text-blue-600 hover:bg-blue-500/10 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                                title="Edit session"
                              >
                                <HiPencil className="text-base" />
                              </button>

                              <button
                                onClick={() => handleDeleteClick(session)}
                                disabled={deletingSessionId === session.id}
                                className="p-2 text-base-content/70 hover:text-red-600 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                                title="Delete session"
                              >
                                {deletingSessionId === session.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                ) : (
                                  <HiTrash className="text-base" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Activities */}
                          <div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                              <h4 className="text-sm font-medium text-base-content font-heading">
                                Activities (
                                {session.ActivityMeasurementList?.length || 0})
                              </h4>
                              {session.ActivityMeasurementList &&
                                session.ActivityMeasurementList.length > 0 &&
                                (() => {
                                  const loggedCount =
                                    session.ActivityMeasurementList.filter(
                                      (m) => !!m.timeLogged,
                                    ).length;
                                  const totalCount =
                                    session.ActivityMeasurementList.length;
                                  const progressPercentage =
                                    (loggedCount / totalCount) * 100;
                                  return (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-base-content/70 font-grotesk whitespace-nowrap">
                                        {loggedCount}/{totalCount} logged
                                      </span>
                                      <div className="w-20 sm:w-24 h-2 bg-base-300 rounded-full overflow-hidden flex-shrink-0">
                                        <div
                                          className="h-full bg-green-500 transition-all duration-300"
                                          style={{
                                            width: `${progressPercentage}%`,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })()}
                            </div>
                            <div className="space-y-2">
                              {session.ActivityMeasurementList?.map(
                                (measurement, index) => {
                                  const isLogged = !!measurement.timeLogged;
                                  return (
                                    <div
                                      key={index}
                                      className={`flex items-center justify-between p-2.5 sm:p-3 rounded-lg border ${
                                        isLogged
                                          ? "bg-green-500/10 border-green-200"
                                          : "bg-base-200 border-base-300"
                                      }`}
                                    >
                                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                        <div
                                          className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            isLogged
                                              ? "bg-green-500 text-white"
                                              : "bg-base-300"
                                          }`}
                                        >
                                          {isLogged && (
                                            <svg
                                              className="w-2.5 h-2.5 sm:w-3 sm:h-3"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          )}
                                        </div>
                                        <span
                                          className={`text-sm sm:text-base font-medium truncate ${
                                            isLogged
                                              ? "text-green-800 dark:text-green-300"
                                              : "text-base-content"
                                          } font-grotesk`}
                                        >
                                          {getActivityTypeName(measurement)}
                                        </span>
                                      </div>

                                      {isLogged ? (
                                        <span className="text-xs font-medium text-green-700 flex-shrink-0 ml-2 font-grotesk">
                                          Logged
                                        </span>
                                      ) : (
                                        <span className="text-xs text-base-content/60 flex-shrink-0 ml-2 font-grotesk">
                                          Planned
                                        </span>
                                      )}
                                    </div>
                                  );
                                },
                              ) || (
                                <p className="text-sm text-base-content/60 font-grotesk">
                                  No activities
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <BottomNav />

      {/* Plan Session Modal */}
      <PlanSessionModal
        isOpen={isPlanSessionModalOpen}
        onClose={() => {
          setIsPlanSessionModalOpen(false);
          setSelectedTemplate(null);
        }}
        onSessionCreated={handleSessionCreated}
        initialTemplate={selectedTemplate}
      />

      {/* Edit Session Modal */}
      <EditSessionModal
        isOpen={isEditSessionModalOpen}
        onClose={() => {
          setIsEditSessionModalOpen(false);
          setSelectedSession(null);
        }}
        session={selectedSession}
        onSessionUpdated={handleSessionUpdated}
      />

      <StartSessionModal
        isOpen={isStartSessionModalOpen}
        onClose={() => setIsStartSessionModalOpen(false)}
        onSessionCreated={handleStartSessionCreated}
      />

      {/* Delete Confirmation Dialog */}
      {sessionToDelete && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleCancelDelete}
        >
          <div
            className="bg-base-100 rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-base-content mb-4 font-heading">
              Delete Session
            </h3>
            <p className="text-base-content/70 mb-6 font-grotesk">
              Are you sure you want to delete the session planned for{" "}
              <strong>{formatDate(sessionToDelete.plannedDate)}</strong>? This
              action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                disabled={deletingSessionId === sessionToDelete.id}
                className="px-4 py-3 text-sm font-medium text-base-content/80 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSession(sessionToDelete)}
                disabled={deletingSessionId === sessionToDelete.id}
                className="px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                {deletingSessionId === sessionToDelete.id
                  ? "Deleting..."
                  : "Delete Session"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sessions;
